'use client';

import { c as _c } from "react/compiler-runtime";
import { assignSalesforceIds, createUsersFromSalesforceRows, lookupSalesforceIds, type AssignSalesforceIdResult, type CreateUsersResult, type LookupSalesforceIdResult, type SalesforceIdImportRow } from '@/app/_workflows/assignSalesforceId';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import { Modal } from '@shared/components/Modal';
import { useServerHandler } from '@shared/hooks/useServerHandler';
import { useCallback, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
interface AssignSalesforceIdModalProps {
  isOpen: boolean;
  onClose(): void;
}
const EMAIL_COL = 'Email';
const ID_COL = 'ID';
const LOCATION_COL = 'Location';
const CONTACT_ID_COL = 'SFContactID';
type Step = 'upload' | 'review' | 'done';
function parseFile(file: File): Promise<SalesforceIdImportRow[]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => {
      try {
        const data = new Uint8Array(e.target!.result as ArrayBuffer);
        const workbook = XLSX.read(data, {
          type: 'array'
        });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, {
          defval: null
        });
        const parsed: SalesforceIdImportRow[] = rows.map(row => ({
          email: String(row[EMAIL_COL] ?? '').trim(),
          sfId: String(row[ID_COL] ?? '').trim(),
          location: String(row[LOCATION_COL] ?? '').trim(),
          contactId: String(row[CONTACT_ID_COL] ?? '').trim()
        })).filter(r => r.email !== '' && r.sfId !== '');
        resolve(parsed);
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });
}
export const AssignSalesforceIdModal: React.FC<AssignSalesforceIdModalProps> = t0 => {
  const $ = _c(45);
  const {
    isOpen,
    onClose
  } = t0;
  const [step, setStep] = useState("upload");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [rows, setRows] = useState(t1);
  const [fileName, setFileName] = useState(null);
  const [parseError, setParseError] = useState(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [lookup, setLookup] = useState(null);
  const [assignResult, setAssignResult] = useState(null);
  const [createResult, setCreateResult] = useState(null);
  const fileInputRef = useRef(null);
  const {
    isLoading,
    doServerAction
  } = useServerHandler();
  let t2;
  if ($[1] !== isLoading || $[2] !== onClose) {
    t2 = () => {
      if (isLoading) {
        return;
      }
      setStep("upload");
      setRows([]);
      setFileName(null);
      setParseError(null);
      setLookup(null);
      setAssignResult(null);
      setCreateResult(null);
      onClose();
    };
    $[1] = isLoading;
    $[2] = onClose;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const handleClose = t2;
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = async file => {
      setParseError(null);
      setRows([]);
      setFileName(file.name);
      try {
        const parsed = await parseFile(file);
        if (parsed.length === 0) {
          setParseError(`No valid rows found. File must have "${EMAIL_COL}" and "${ID_COL}" columns.`);
          return;
        }
        setRows(parsed);
      } catch {
        setParseError("Failed to parse file. Make sure it is a valid Excel or CSV file.");
      }
    };
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const processFile = t3;
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = e => {
      e.preventDefault();
      setIsDragOver(false);
      const file_0 = e.dataTransfer.files[0];
      if (file_0) {
        processFile(file_0);
      }
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const handleDrop = t4;
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = e_0 => {
      const file_1 = e_0.target.files?.[0];
      if (file_1) {
        processFile(file_1);
      }
      e_0.target.value = "";
    };
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const handleFileChange = t5;
  let t6;
  if ($[7] !== doServerAction || $[8] !== rows) {
    t6 = () => {
      doServerAction(async () => ({
        data: await lookupSalesforceIds(rows)
      }), result => {
        if (!result || typeof result !== "object" || !("data" in result)) {
          return;
        }
        setLookup(result.data);
        setStep("review");
      });
    };
    $[7] = doServerAction;
    $[8] = rows;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const handleLookup = t6;
  let t7;
  if ($[10] !== doServerAction || $[11] !== lookup) {
    t7 = () => {
      if (!lookup) {
        return;
      }
      doServerAction(async () => {
        const [assign, create] = await Promise.all([assignSalesforceIds(lookup.found), lookup.notFound.length > 0 ? createUsersFromSalesforceRows(lookup.notFound) : Promise.resolve(null)]);
        return {
          data: {
            assign,
            create
          }
        };
      }, result_0 => {
        if (!result_0 || typeof result_0 !== "object" || !("data" in result_0)) {
          return;
        }
        setAssignResult(result_0.data.assign);
        setCreateResult(result_0.data.create);
        setStep("done");
      });
    };
    $[10] = doServerAction;
    $[11] = lookup;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const handleConfirm = t7;
  let t8;
  if ($[13] !== handleClose || $[14] !== handleConfirm || $[15] !== handleLookup || $[16] !== isLoading || $[17] !== lookup || $[18] !== rows || $[19] !== step) {
    t8 = () => {
      if (step === "upload") {
        return <Button variant="contained" disabled={rows.length === 0 || isLoading} onClick={handleLookup}>Next {rows.length > 0 ? `(${rows.length} rows)` : ""}</Button>;
      }
      if (step === "review") {
        return <Box sx={{
          display: "flex",
          gap: 1
        }}><Button variant="outlined" disabled={isLoading} onClick={() => setStep("upload")}>Back</Button><Button variant="contained" disabled={!lookup || lookup.found.length === 0 && lookup.notFound.length === 0 || isLoading} onClick={handleConfirm}>{"Confirm & Save"}{lookup ? ` (${lookup.found.length} update${lookup.found.length !== 1 ? "s" : ""}${lookup.notFound.length > 0 ? `, ${lookup.notFound.length} new` : ""})` : ""}</Button></Box>;
      }
      return <Button variant="contained" onClick={handleClose}>Close</Button>;
    };
    $[13] = handleClose;
    $[14] = handleConfirm;
    $[15] = handleLookup;
    $[16] = isLoading;
    $[17] = lookup;
    $[18] = rows;
    $[19] = step;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const renderActions = t8;
  let t9;
  if ($[21] !== renderActions) {
    t9 = renderActions();
    $[21] = renderActions;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {};
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== fileName || $[25] !== isDragOver || $[26] !== parseError || $[27] !== rows || $[28] !== step) {
    t11 = step === "upload" && <><Box onDrop={handleDrop} onDragOver={e_1 => {
        e_1.preventDefault();
        setIsDragOver(true);
      }} onDragLeave={() => setIsDragOver(false)} onClick={() => fileInputRef.current?.click()} sx={{
        mt: 1,
        mb: 2,
        border: 2,
        borderStyle: "dashed",
        borderColor: isDragOver ? "primary.main" : "divider",
        borderRadius: 2,
        p: 4,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 1,
        cursor: "pointer",
        backgroundColor: isDragOver ? "action.hover" : "background.paper",
        transition: "all 0.15s ease"
      }}><CloudUploadIcon sx={{
          fontSize: 40,
          color: "text.secondary"
        }} /><Typography variant="body2" color="text.secondary">{"Drag & drop or click to select a file"}</Typography><Typography variant="caption" color="text.disabled">Supports .xlsx, .xls, .csv</Typography><input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" style={{
          display: "none"
        }} onChange={handleFileChange} /></Box>{parseError && <Alert severity="error" sx={{
        mb: 2
      }}>{parseError}</Alert>}{rows.length > 0 && <Box sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        flexWrap: "wrap"
      }}><Chip label={`${rows.length} rows ready`} color="success" size="small" />{fileName && <Typography variant="caption" color="text.secondary">{fileName}</Typography>}</Box>}</>;
    $[24] = fileName;
    $[25] = isDragOver;
    $[26] = parseError;
    $[27] = rows;
    $[28] = step;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== lookup || $[31] !== step) {
    t12 = step === "review" && lookup && <Box sx={{
      display: "flex",
      flexDirection: "column",
      gap: 2
    }}><Alert severity={lookup.notFound.length > 0 ? "warning" : "success"}>{lookup.found.length} user{lookup.found.length !== 1 ? "s" : ""} found{lookup.notFound.length > 0 ? `, ${lookup.notFound.length} not found` : ""}. Review and confirm to save.</Alert>{lookup.found.length > 0 && <Box><Typography variant="subtitle2" color="success.main" gutterBottom={true}>Found ({lookup.found.length})</Typography><Divider sx={{
          mb: 1
        }} /><List dense={true} disablePadding={true}>{lookup.found.map(_temp)}</List></Box>}{lookup.notFound.length > 0 && <Box><Typography variant="subtitle2" color="warning.main" gutterBottom={true}>Not found — will be created ({lookup.notFound.length})</Typography><Typography variant="caption" color="text.secondary">New users are created with QMT access only, using the site from the "{LOCATION_COL}" column.</Typography><Divider sx={{
          my: 1
        }} /><List dense={true} disablePadding={true}>{lookup.notFound.map(_temp2)}</List></Box>}</Box>;
    $[30] = lookup;
    $[31] = step;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== assignResult || $[34] !== createResult || $[35] !== step) {
    t13 = step === "done" && (assignResult || createResult) && <Box sx={{
      display: "flex",
      flexDirection: "column",
      gap: 2
    }}><Box sx={{
        display: "flex",
        alignItems: "center",
        gap: 1
      }}><CheckCircleOutlineIcon color={assignResult?.errors.length || createResult?.errors.length ? "warning" : "success"} /><Typography variant="body1" fontWeight={500}>Assignment complete</Typography></Box>{assignResult && <Alert severity={assignResult.errors.length > 0 ? "warning" : "success"}>{assignResult.updated} user{assignResult.updated !== 1 ? "s" : ""} updated successfully.</Alert>}{assignResult && assignResult.errors.length > 0 && <Box><Typography variant="subtitle2" color="error.main" gutterBottom={true}>Update errors ({assignResult.errors.length})</Typography><Divider sx={{
          mb: 1
        }} /><List dense={true} disablePadding={true}>{assignResult.errors.map(_temp3)}</List></Box>}{createResult && <Alert severity={createResult.errors.length > 0 ? "warning" : "success"}>{createResult.created} user{createResult.created !== 1 ? "s" : ""} created.</Alert>}{createResult && createResult.errors.length > 0 && <Box><Typography variant="subtitle2" color="error.main" gutterBottom={true}>Create errors ({createResult.errors.length})</Typography><Divider sx={{
          mb: 1
        }} /><List dense={true} disablePadding={true}>{createResult.errors.map(_temp4)}</List></Box>}</Box>;
    $[33] = assignResult;
    $[34] = createResult;
    $[35] = step;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  let t14;
  if ($[37] !== handleClose || $[38] !== isLoading || $[39] !== isOpen || $[40] !== t11 || $[41] !== t12 || $[42] !== t13 || $[43] !== t9) {
    t14 = <Modal isOpen={isOpen} onClose={handleClose} title="Assign salesforceId from excel" subTitle={"Upload an Excel or CSV file with \"Email\", \"ID\" and \"Location\" columns. Users are matched by email; the ID is saved as their salesforceId."} maxWidth="sm" fullWidth={true} disableClose={isLoading} isLoading={isLoading} actions={t9} ContentProps={t10}>{t11}{t12}{t13}</Modal>;
    $[37] = handleClose;
    $[38] = isLoading;
    $[39] = isOpen;
    $[40] = t11;
    $[41] = t12;
    $[42] = t13;
    $[43] = t9;
    $[44] = t14;
  } else {
    t14 = $[44];
  }
  return t14;
};
function _temp(u) {
  return <ListItem key={u.id} disableGutters={true}><ListItemText primary={`${u.name} — salesforceId: ${u.sfId}${u.contactId ? `, contactId: ${u.contactId}` : ""}`} secondary={u.email} primaryTypographyProps={{
      variant: "body2"
    }} secondaryTypographyProps={{
      variant: "caption",
      color: "text.secondary"
    }} /></ListItem>;
}
function _temp2(row) {
  return <ListItem key={row.email} disableGutters={true}><ListItemText primary={row.email} secondary={`${row.location || "No location set"}${row.contactId ? ` — contactId: ${row.contactId}` : ""}`} primaryTypographyProps={{
      variant: "body2",
      color: "text.secondary"
    }} secondaryTypographyProps={{
      variant: "caption",
      color: "text.disabled"
    }} /></ListItem>;
}
function _temp3(email) {
  return <ListItem key={email} disableGutters={true}><ListItemText primary={email} primaryTypographyProps={{
      variant: "body2",
      color: "error"
    }} /></ListItem>;
}
function _temp4(err) {
  return <ListItem key={err} disableGutters={true}><ListItemText primary={err} primaryTypographyProps={{
      variant: "body2",
      color: "error"
    }} /></ListItem>;
}